// Copyright © 2025 The Things Industries B.V.

import React, { useCallback } from 'react'
import classNames from 'classnames'

import { IconRefresh } from '@ttn-lw/components/icon'
import Input from '@ttn-lw/components/input'
import Form, { useFormContext } from '@ttn-lw/components/form'
import Button from '@ttn-lw/components/button'

import { generateRandomHexColor } from '@console/components/pill/utils.tti'
import Pill from '@console/components/pill/index.tti'

import PropTypes from '@ttn-lw/lib/prop-types'
import sharedMessages from '@ttn-lw/lib/shared-messages'

import labelFormValidation from './label-form-validation.tti'

import style from './labels-table.tti.styl'

const InnerForm = ({ handleInputChange, setRandomColor }) => {
  const { values, setFieldValue } = useFormContext()

  const handleGenerateRandomHexColor = useCallback(() => {
    const randomColor = generateRandomHexColor()
    setRandomColor(randomColor)
    setFieldValue('color', randomColor)
    handleInputChange('color', randomColor)
  }, [setRandomColor, setFieldValue, handleInputChange])

  const handleNameChange = useCallback(
    value => {
      handleInputChange('name', value)
    },
    [handleInputChange],
  )

  const handleDescriptionChange = useCallback(
    value => {
      handleInputChange('description', value)
    },
    [handleInputChange],
  )

  const handleColorChange = useCallback(
    value => {
      handleInputChange('color', value)
    },
    [handleInputChange],
  )

  return (
    <>
      <Pill color={values.color} text={values.name === '' ? 'Label preview' : values.name} />
      <div className="d-flex al-start j-between gap-cs-xl mt-cs-xl">
        <Form.Field
          title={sharedMessages.name}
          name="name"
          onChange={handleNameChange}
          component={Input}
          required
          autoFocus
          fieldWidth="s"
        />
        <div className="d-flex gap-cs-xs al-start">
          <Button
            icon={IconRefresh}
            secondary
            onClick={handleGenerateRandomHexColor}
            className={style.generateButton}
            type="button"
          />
          <Form.Field
            title={sharedMessages.color}
            name="color"
            onChange={handleColorChange}
            component={Input}
            required
          />
        </div>
      </div>
      <Form.Field
        title={sharedMessages.description}
        name="description"
        onChange={handleDescriptionChange}
        component={Input}
        type="textarea"
        inputWidth="l"
        className="flex-basis-1"
      />
    </>
  )
}

InnerForm.propTypes = {
  handleInputChange: PropTypes.func.isRequired,
  setRandomColor: PropTypes.func.isRequired,
}

const ModalForm = ({
  initialValues,
  handleInputChange,
  randomColor,
  setRandomColor,
  className,
  error,
}) => (
  <Form
    error={error}
    smallError
    initialValues={initialValues}
    validationSchema={labelFormValidation}
    className={classNames(className, 'd-flex direction-column gap-cs-xs')}
  >
    <InnerForm
      handleInputChange={handleInputChange}
      randomColor={randomColor}
      setRandomColor={setRandomColor}
    />
  </Form>
)

ModalForm.propTypes = {
  className: PropTypes.string,
  error: PropTypes.error,
  handleInputChange: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string,
  }).isRequired,
  randomColor: PropTypes.string.isRequired,
  setRandomColor: PropTypes.func.isRequired,
}

ModalForm.defaultProps = {
  className: undefined,
  error: undefined,
}

export default ModalForm
